import { Controller } from "@hotwired/stimulus";

// Drives a turbo-frame's `src` from an external filter form sitting OUTSIDE the frame.
//
// Use when the search/tab/filter UI must stay focused while only the results swap.
// The controller's element should be a <form>. Inputs trigger reloads via:
//   data-action="input->frame-filter#changeDebounced"   (text inputs)
//   data-action="change->frame-filter#change"           (selects, radios, checkboxes)
//
// Values:
//   frame: ID of the turbo-frame to update
//   url:   base path to navigate the frame to (defaults to current path)
//   debounce: debounce ms for `changeDebounced` (default 300)
//   pushHistory: if true (default), updates the browser URL via replaceState
export default class extends Controller {
  static values = {
    frame: String,
    url: { type: String, default: "" },
    debounce: { type: Number, default: 300 },
    pushHistory: { type: Boolean, default: true }
  };

  connect() {
    this.debounceTimer = null;
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }

  change() {
    this.submit();
  }

  changeDebounced() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.submit(), this.debounceValue);
  }

  submit() {
    const frame = document.getElementById(this.frameValue);
    if (!frame) return;

    const basePath = this.urlValue || window.location.pathname;
    const url = new URL(basePath, window.location.origin);

    // Preserve sort params already on the page (sort headers live inside the frame).
    const current = new URL(window.location.href);
    for (const key of ["column", "direction"]) {
      const value = current.searchParams.get(key);
      if (value) url.searchParams.set(key, value);
    }

    // Append all non-empty form values.
    const formData = new FormData(this.element);
    for (const [key, value] of formData.entries()) {
      if (value !== "" && value !== null) {
        url.searchParams.append(key, value);
      }
    }

    if (this.pushHistoryValue) {
      window.history.replaceState({}, "", url.toString());
    }

    frame.src = url.toString();
  }
}
